/**
 * Contrato de "onde o ato aconteceu" e dos mapas do exame eletrônico (E90.F2/F3).
 *
 * Espelha apps/api/types/exame_eletronico_localizacao.py e
 * apps/api/types/exame_eletronico_mapas.py. Módulo próprio, e não mais tipos em
 * `lib/exame_eletronico` (já perto do teto de linhas): quem precisa destes tipos
 * importa daqui, e o resultado do exame os referencia.
 *
 * Tudo aqui é descritivo (decisão do owner, 2026-09-13): nada disso muda grau,
 * tendência ou red flags — a tela apenas mostra.
 */
import type { AxiosInstance } from 'axios';

import { classifyExameEletronicoError, type SituacaoItem } from '@/lib/exame_eletronico';
import type { FaixaDistancia } from '@/lib/exame_eletronico_enriquecimento';
import type { PapelIp } from '@/lib/exame_eletronico_protocolo';

/** O raio da régua `v8-local` (ADR-0075 emenda o), em km — o mesmo do backend. */
export const RAIO_COBERTURA_KM = 50;

export type CoberturaDoEnderecoDeRede =
  | 'cobre'
  | 'nao_cobre'
  | 'nao_conclusivo'
  | 'indeterminada'
  | 'nao_se_aplica';

export type PrecisaoDoEnderecoDeRede =
  | 'muito_alta'
  | 'alta'
  | 'media'
  | 'baixa'
  | 'muito_baixa'
  | 'nao_informada';

export interface LocalDoAtoData {
  municipio?: string | null;
  estado?: string | null;
  situacao: SituacaoItem;
  detalhe?: string | null;
  consulta_id?: string | null;
}

export interface EnderecoDeclaradoAvaliadoData {
  endereco: string;
  municipio?: string | null;
  estado?: string | null;
  latitude?: number | null;
  longitude?: number | null;
  precisao_geocodificacao?: string | null;
  distancia_m?: number | null;
  faixa: FaixaDistancia;
  situacao: SituacaoItem;
  detalhe?: string | null;
  consulta_id?: string | null;
  /** E112.F3 (D-09) — a confiança que o geocodificador declara, de 0 a 1. */
  confianca_geocodificacao?: number | null;
}

export interface LocalizacaoDoEnderecoDeRedeData {
  endereco: string;
  papel: PapelIp;
  cidade?: string | null;
  estado?: string | null;
  pais?: string | null;
  latitude?: number | null;
  longitude?: number | null;
  precisao: PrecisaoDoEnderecoDeRede;
  distancia_m?: number | null;
  cobertura: CoberturaDoEnderecoDeRede;
  detalhe?: string | null;
  consulta_id?: string | null;
}

export interface LocalizacaoApuradaData {
  local_do_ato: LocalDoAtoData;
  endereco_declarado?: EnderecoDeclaradoAvaliadoData | null;
  enderecos_de_rede: LocalizacaoDoEnderecoDeRedeData[];
}

export type TipoDoMapa = 'local_do_ato' | 'confronto';
export type PapelDoMarcador = 'ato' | 'endereco_declarado' | 'endereco_de_rede';

export interface MarcadorDoMapaData {
  letra: string;
  papel: PapelDoMarcador;
  referencia: string;
  latitude: number;
  longitude: number;
}

export interface MapaDoExameData {
  tipo: TipoDoMapa;
  situacao: SituacaoItem;
  detalhe?: string | null;
  consulta_id?: string | null;
  imagem_sha256?: string | null;
  marcadores: MarcadorDoMapaData[];
  raio_m?: number | null;
}

/**
 * Baixa a imagem de um mapa. A rota exige autenticação no cabeçalho, então a
 * imagem não vai direto no `src` — quem chama monta a URL local.
 */
export async function baixarMapaDoExame(
  client: AxiosInstance,
  caseId: string,
  consultaId: string,
): Promise<Blob> {
  try {
    const { data } = await client.get<Blob>(
      `/casos/${caseId}/exame-eletronico/mapas/${consultaId}`,
      { responseType: 'blob' },
    );
    return data;
  } catch (err) {
    throw classifyExameEletronicoError(err);
  }
}
